import { __assign } from '../../_virtual/_tslib.js';
import React__default, { createContext, isValidElement, useState, useRef, useImperativeHandle, useEffect, cloneElement, useContext, useCallback, useLayoutEffect } from 'react';
import ReactDOM__default from 'react-dom';
import { classNames } from '../../helpers/classnames.js';
import { PopupsContext } from '../../helpers/popupsContext.js';
import { r as reactIsExports } from '../../node_modules/react-is/index.js';

var PopoverContext = createContext({
    applicationRootElementId: 'root',
    popoverRootElementID: 'dsb_popover',
});
var Popover = function (_a) {
    var _b = _a.open, open = _b === void 0 ? false : _b, content = _a.content, _c = _a.disableHover, disableHover = _c === void 0 ? false : _c, popoverClassName = _a.popoverClassName, children = _a.children, noInteract = _a.noInteract, _d = _a.dataTestId, dataTestId = _d === void 0 ? 'Popover' : _d;
    if (!isValidElement(children)) {
        throw new Error('Popover children is not valid element!');
    }
    if (!reactIsExports.isForwardRef(children) && !children.ref) {
        throw new Error('Popover children don`t have ref props. Use React.forwardRef() ' +
            'on children or children must have ref props! ' +
            'Example: https://ru.reactjs.org/docs/forwarding-refs.html');
    }
    var _e = useState(open), isVisible = _e[0], setVisibility = _e[1];
    var _f = useState(new DOMRect()), wrappedContentRect = _f[0], setWrappedContentRect = _f[1];
    var targetRef = useRef(null);
    useImperativeHandle(children.ref, function () { return targetRef.current; });
    useEffect(function () {
        var targetEl = targetRef.current;
        // let showParameter = true;
        // if ('disabled' in targetEl && targetEl.disabled) {
        // 	showParameter = false;
        // } else {
        // 	showParameter = true;
        // }
        var handleMouseEnter = function () {
            setVisible(true);
        };
        var handleMouseLeave = function () {
            setVisible(false);
        };
        if (!disableHover) {
            targetEl === null || targetEl === void 0 ? void 0 : targetEl.addEventListener('mouseenter', handleMouseEnter);
            targetEl === null || targetEl === void 0 ? void 0 : targetEl.addEventListener('mouseleave', handleMouseLeave);
        }
        else {
            setVisible(false);
        }
        return function () {
            targetEl === null || targetEl === void 0 ? void 0 : targetEl.removeEventListener('mouseenter', handleMouseEnter);
            targetEl === null || targetEl === void 0 ? void 0 : targetEl.removeEventListener('mouseleave', handleMouseLeave);
        };
    }, [disableHover, children]);
    useEffect(function () {
        setVisible(open);
    }, [open]);
    var setVisible = function (isVisible) {
        var targetEl = targetRef.current;
        if (isVisible && targetEl) {
            setWrappedContentRect(targetEl.getBoundingClientRect());
        }
        setVisibility(isVisible);
    };
    return (React__default.createElement(React__default.Fragment, null,
        cloneElement(children, __assign(__assign({}, children.props), { ref: targetRef })),
        isVisible && (React__default.createElement(PopoverPortal, { noInteract: noInteract, content: content, wrappedContentRect: wrappedContentRect, className: popoverClassName, dataTestId: dataTestId }))));
};
var PopoverPortal = function (_a) {
    var content = _a.content, wrappedContentRect = _a.wrappedContentRect, className = _a.className, _b = _a.noInteract, noInteract = _b === void 0 ? true : _b, _c = _a.dataTestId, dataTestId = _c === void 0 ? 'PopoverContent' : _c;
    var rootRef = React__default.useRef(null);
    var _d = React__default.useState({ x: 0, y: 0 }), coords = _d[0], setCoords = _d[1];
    var _e = useContext(PopoverContext), applicationRootElementId = _e.applicationRootElementId, popoverRootElementID = _e.popoverRootElementID;
    var _f = useContext(PopupsContext), applicationElementID = _f.applicationElementID, rootElement = _f.rootElement;
    var appElementID = applicationRootElementId || applicationElementID || 'root';
    var getCoords = useCallback(function () {
        var rootObj = rootRef.current;
        var contentRect = wrappedContentRect;
        var popoverRect = rootObj.getBoundingClientRect();
        var _a = document.documentElement, clientHeight = _a.clientHeight, clientWidth = _a.clientWidth;
        // TODO заменить на токен, когда он появится
        var MARGIN = 8;
        var placedOnMiddleY = false;
        var calcY = function () {
            var needPlaceOnTop = popoverRect.bottom + MARGIN <= contentRect.top;
            var needPlaceOnBottom = popoverRect.height + MARGIN <= clientHeight - contentRect.bottom;
            if (needPlaceOnBottom) {
                return contentRect.bottom + MARGIN;
            }
            if (needPlaceOnTop) {
                return contentRect.top - (MARGIN + popoverRect.height);
            }
            placedOnMiddleY = true;
            return contentRect.y + contentRect.height / 2 - popoverRect.height / 2;
        };
        var calcX = function (placedOnMiddleY) {
            // Нет места ни сверху ни снизу -> считаем горизотальные отступы по бокам.
            if (placedOnMiddleY) {
                // Для тела тултипа есть место слева
                if (contentRect.left > clientWidth - contentRect.right) {
                    return contentRect.left - popoverRect.width - MARGIN;
                }
                // Для тела тултипа есть место справа
                return contentRect.right + MARGIN;
            }
            var middleOfContent = contentRect.left + contentRect.width / 2;
            var middleOfPopover = popoverRect.width / 2;
            var coordOfPopover = middleOfContent - middleOfPopover;
            if (coordOfPopover <= 0) {
                return 0;
            }
            if (coordOfPopover >= clientWidth - popoverRect.width) {
                return clientWidth - popoverRect.width;
            }
            return coordOfPopover;
        };
        var y = calcY();
        var x = calcX(placedOnMiddleY);
        return { x: x, y: y };
    }, [wrappedContentRect]);
    var createPortalRoot = function (rootId) {
        var rootElement = document.createElement('div');
        rootElement.id = rootId;
        rootElement.className = classNames('dsb_popover', noInteract && 'dsb_popover__no-interact');
        rootElement.setAttribute('style', "left: ".concat(coords.x, "px; top: ").concat(coords.y, "px; position: fixed;"));
        return rootElement;
    };
    var mount = rootElement || document.getElementById(appElementID);
    if (!mount) {
        throw new Error("Application root element with ID \"".concat(appElementID, "\" not found"));
    }
    var el = createPortalRoot(popoverRootElementID);
    el.classList.add('inverseTheme');
    useLayoutEffect(function () {
        mount.appendChild(el);
        return function () {
            mount.removeChild(el);
        };
    }, [el, mount]);
    useLayoutEffect(function () {
        setCoords(getCoords());
    }, [rootRef, getCoords]);
    var popoverContent = (React__default.createElement("div", { ref: rootRef, className: classNames('dsb_popover-content', className), "data-testid": dataTestId }, content));
    return ReactDOM__default.createPortal(popoverContent, el);
};

export { Popover, PopoverContext, PopoverPortal };
